import { inject, Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Actions } from '@ngrx/effects';
import moment from 'moment';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { TravelStatusUpdateInterface } from '../../interfaces/travel-status-update.interface';
import { TravelStatusUpdateModel } from '../../models/complex/travel-status-update.model';
import { getUuidV4 } from '../../utilities/generic-utilities';
import { selectEarlyTravelHours } from '../../state/config/selectors/config.selector';
import { travelStatusModelToRequest } from '../../state/travel-status-update/travel-status-update.converters';
import {
    submitTravelStatusRequested,
    travelStatusSubmitAborted,
    travelStatusSubmitFailed,
    travelStatusSubmitSucceeded,
} from '../../state/travel-status-update/actions/travel-status-update.actions';
import { isEarlyTravelRuleViolated } from './travel-status-update.rules';

/**
 * NgRx-backed implementation of {@link TravelStatusUpdateInterface}. A pure dispatcher — it holds
 * no state and contains no orchestration logic. Everything the legacy service did inline (the
 * modals, the router navigation, the app notifications, the OData calls) lives in
 * `TravelStatusUpdateEffects`; this service only translates between the legacy
 * `TravelStatusUpdateModel` and the slice's plain state types at the public method boundary.
 */
@Injectable({ providedIn: 'root' })
export class TravelStatusUpdateNgrxService implements TravelStatusUpdateInterface {
    private readonly store = inject(Store);
    private readonly actions$ = inject(Actions);

    public get earlyTravelHoursAllowed(): number {
        return this.store.selectSignal(selectEarlyTravelHours)();
    }

    public submitStatus(travelStatusModel: TravelStatusUpdateModel): Observable<TravelStatusUpdateModel> {
        const correlationId = getUuidV4();
        return this.awaitOutcome(
            submitTravelStatusRequested({ request: travelStatusModelToRequest(travelStatusModel), correlationId }),
            correlationId,
            travelStatusSubmitSucceeded.type,
            travelStatusSubmitFailed.type,
            travelStatusSubmitAborted.type,
        ).pipe(map(() => travelStatusModel));
    }

    public checkForEarlyTravelRuleViolation(plannedStart: moment.Moment, actualDate: moment.Moment): boolean {
        return isEarlyTravelRuleViolated(plannedStart, actualDate, this.earlyTravelHoursAllowed);
    }

    /**
     * Dispatches `request` and completes once the matching `succeededType`/`failedType` outcome
     * arrives, filtered by `correlationId`. The outcome subscription is set up before dispatching so
     * a synchronous result cannot be missed — the same pattern as `ConfirmationNgrxService.awaitOutcome`.
     *
     * @param request the action to dispatch
     * @param correlationId the id carried by the request and echoed by every outcome action
     * @param succeededType the outcome action type that resolves the observable
     * @param failedType the outcome action type (carrying an `error` field) that errors it
     * @param abortedType the outcome type that completes without emitting
     */
    private awaitOutcome<T extends Action = Action>(
        request: Action,
        correlationId: string,
        succeededType: string,
        failedType: string,
        abortedType: string,
    ): Observable<T> {
        return new Observable<T>((subscriber) => {
            const subscription = this.actions$
                .pipe(
                    filter(
                        (action: Action & { correlationId?: string }) =>
                            (action.type === succeededType ||
                                action.type === failedType ||
                                action.type === abortedType) &&
                            action.correlationId === correlationId,
                    ),
                    take(1),
                    map((action) => {
                        if (action.type === failedType) {
                            throw new Error((action as Action & { error: string }).error);
                        }
                        return action as T;
                    }),
                    filter((action) => action.type !== abortedType),
                )
                .subscribe(subscriber);
            this.store.dispatch(request);
            return () => subscription.unsubscribe();
        });
    }
}
